import { cn } from "@/lib/utils";
import { SERIES_BG, TRACK_BG } from "@/components/dashboard/widgets/opsColors";
import { barWidths, fmtCount } from "./listMath";

export interface ChartChip { key: string; label: string; count: number }
export const ALL_KEY = "all";

/**
 * Filter chips that are also a bar chart: each chip shows its count and a thin
 * bar sized against the largest chip. A chip with 0 is hidden unless it is the
 * one selected, so a search that empties the selected group still shows why.
 */
export function ChartChips({ chips, value, onChange, ariaLabel, allLabel = "All", allCount }: {
  chips: ChartChip[];
  value: string;
  onChange: (key: string) => void;
  ariaLabel: string;
  allLabel?: string;
  allCount?: number;
}) {
  const shown = chips.filter((c) => c.count > 0 || c.key === value);
  const total = allCount ?? chips.reduce((s, c) => s + c.count, 0);
  const widths = barWidths(shown.map((c) => c.count));
  const chip = (key: string, label: string, count: number, width: number) => (
    <button
      key={key} type="button" aria-pressed={value === key} aria-label={`${label}: ${fmtCount(count)}`} onClick={() => onChange(key)}
      className={cn(
        "inline-flex shrink-0 flex-col gap-0.5 min-w-[4.75rem] rounded-md border bg-card px-2 pt-1 pb-1.5 text-left transition-colors",
        value === key ? "border-primary ring-1 ring-primary" : "border-border hover:border-foreground/30",
      )}
    >
      <span className="flex items-baseline justify-between gap-2 text-[11px] text-muted-foreground whitespace-nowrap">
        <span>{label}</span>
        <b className="font-semibold text-foreground tabular-nums">{fmtCount(count)}</b>
      </span>
      <span className={cn("h-1 rounded-full overflow-hidden", TRACK_BG)}>
        <span className={cn("block h-full rounded-full", SERIES_BG)} style={{ width: `${width}%` }} />
      </span>
    </button>
  );
  return (
    <div role="group" aria-label={ariaLabel} className="flex items-center gap-1.5 overflow-x-auto min-w-0">
      {chip(ALL_KEY, allLabel, total, total > 0 ? 100 : 0)}
      {shown.map((c, i) => chip(c.key, c.label, c.count, widths[i]))}
    </div>
  );
}
